import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Sparkles, ThumbsUp, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authApi } from "@/context/AuthContext";

export const MatchedClubs = () => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [hidden, setHidden] = useState([]);

  const refresh = useCallback(() => {
    authApi.get("/me/club-matches").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    authApi.get("/me/match-feedback").then((r) => setHidden(r.data.hidden)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || (!user.city && !user.pace)) {
      setItems(null);
      return;
    }
    refresh();
  }, [user, refresh]);

  const sendFeedback = (slug, action) =>
    authApi.post("/me/match-feedback", { club_slug: slug, action }).catch(() => {});

  const toggleLike = async (e, club) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !club.liked;
    setItems((prev) => prev.map((c) => (c.slug === club.slug ? { ...c, liked: next } : c)));
    await sendFeedback(club.slug, next ? "up" : "clear");
    refresh();
    if (next) toast.success("Noted. More clubs like this one.");
  };

  const hide = async (e, club) => {
    e.preventDefault();
    e.stopPropagation();
    setItems((prev) => prev.filter((c) => c.slug !== club.slug));
    setHidden((prev) => [...prev, club.slug]);
    await sendFeedback(club.slug, "hide");
    toast(`${club.name.toUpperCase()} hidden from matches.`, {
      action: {
        label: "UNDO",
        onClick: async () => {
          await sendFeedback(club.slug, "clear");
          refresh();
        },
      },
    });
  };

  const showHidden = async () => {
    await Promise.all(hidden.map((slug) => sendFeedback(slug, "clear")));
    refresh();
    toast.success("Hidden clubs are back in the mix.");
  };

  if (!user) return null;

  if (!user.city && !user.pace) {
    return (
      <div className="border border-dashed border-white/15 px-5 py-4 mb-8 flex flex-wrap items-center justify-between gap-3" data-testid="matched-clubs-cta">
        <p className="text-white/50 text-sm">Set your home city and pace to get club matches picked for you.</p>
        <Link
          to="/profile"
          data-testid="matched-clubs-profile-link"
          className="font-mono-label border border-white/25 px-4 py-2.5 text-white/70 hover:border-white hover:text-white transition-colors duration-200"
        >
          COMPLETE MY PROFILE
        </Link>
      </div>
    );
  }

  if (!items || items.length === 0) return null;

  return (
    <section className="mb-10" data-testid="matched-clubs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono-label text-white/40 flex items-center gap-2">
          <Sparkles size={13} /> MATCHED FOR YOU{user.city ? ` · ${user.city.toUpperCase()}` : ""}{user.pace ? ` · ${user.pace} /MI` : ""}
        </p>
        {hidden.length > 0 && (
          <button
            type="button"
            data-testid="matched-clubs-show-hidden"
            onClick={showHidden}
            className="font-mono-label flex items-center gap-1.5 text-white/40 hover:text-white transition-colors duration-200"
          >
            <Eye size={12} /> SHOW HIDDEN ({hidden.length})
          </button>
        )}
      </div>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((c) => (
          <Link
            key={c.slug}
            to={`/clubs/${c.slug}`}
            data-testid={`matched-club-${c.slug}`}
            className="group relative border border-white/10 hover:border-white/50 px-5 py-4 transition-colors duration-200"
          >
            <div className="absolute top-3 right-3 flex items-center gap-1">
              <button
                type="button"
                data-testid={`matched-club-up-${c.slug}`}
                onClick={(e) => toggleLike(e, c)}
                title={c.liked ? "Liked — click to undo" : "More like this"}
                className={`p-1.5 transition-colors duration-150 ${c.liked ? "text-white" : "text-white/30 hover:text-white"}`}
              >
                <ThumbsUp size={13} fill={c.liked ? "currentColor" : "none"} />
              </button>
              <button
                type="button"
                data-testid={`matched-club-hide-${c.slug}`}
                onClick={(e) => hide(e, c)}
                title="Hide from matches"
                className="p-1.5 text-white/30 hover:text-white transition-colors duration-150"
              >
                <X size={13} />
              </button>
            </div>
            <p className="font-display text-lg leading-tight flex items-center gap-1.5 pr-14">
              <span className="truncate">{c.name.toUpperCase()}</span>
              {c.verified && <BadgeCheck size={14} className="shrink-0 text-white/50" />}
            </p>
            <p className="text-white/40 text-xs mt-1">
              {c.city}{c.state ? `, ${c.state}` : ""}{c.neighborhood ? ` · ${c.neighborhood}` : ""}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(c.match_why || []).map((w) => (
                <span key={w} className="font-mono-label text-[10px] border border-white/15 px-2 py-1 text-white/50">{w}</span>
              ))}
            </div>
          </Link>
        ))}
      </div>
      <div className="border-b border-white/10 mt-8" />
    </section>
  );
};
